import React, { useEffect, useState } from 'react';
import { FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import CustomInput from '../../Components/CommonComponents/CustomInput';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getJwtToken, END_POINT } from '../../Redux/AdminReducer/action';
import { useNavigate, useParams } from 'react-router-dom';
import CustomSpinner from '../../Components/CommonComponents/CustomSpinner';
import axios from 'axios';
import CustomSelect from '../../Components/CommonComponents/CustomSelect';
import { downloadReturnPdf } from './orderPdf';
import MedicineNameField from './MedicineNameField';

const fetchSingleItem = async (id) => {
  try {
    const result = await axios.get(`${END_POINT}/inventory/orders/${id}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.log(error);

    toast.error('Something went wrong while fetching data');
  }
};

const fetchOrders = async (ids) => {
  if (!ids) return { data: [] };
  try {
    const params = ids
      ?.split(',')
      ?.map((id) => `orders=${encodeURIComponent(id)}`)
      .join('&');

    const result = await axios.get(`${END_POINT}/inventory/ordersIds?${params}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.error(error);
    toast.error('Something went wrong while fetching data');
  }
};

const fetchAllDistributors = async () => {
  try {
    const result = await axios.get(`${END_POINT}/inventory/distributors`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    console.error(error);
  }
};

const fetchDistributor = async (cname) => {
  // One entry per company, not per medicine: a low-stock order can hold
  // thousands of rows but only a handful of companies, and repeating them
  // overflows the URL. Medicines with no company would send "undefined".
  const companies = [...new Set((cname || []).map((c) => (c ?? '').trim()).filter(Boolean))];
  const params = companies.map((id) => `mname=${encodeURIComponent(id)}`).join('&');

  try {
    const result = await axios.get(`${END_POINT}/inventory/distributorsByMedicine?${params}`, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    return result;
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    console.error(error);
    toast.error('Something went wrong while fetching data');
  }
};
// One submission = one return = one Return_Id, whatever the number of
// medicines. Nothing is emailed; the PDF that downloads here is the return copy.
const createItem = async (items, navigate, setIsPorcessing) => {
  try {
    const result = await axios.post(`${END_POINT}/inventory/returns`, items, {
      headers: {
        Authorization: getJwtToken(),
      },
    });

    const { Return_Id, items: createdItems } = result?.data || {};
    toast.success(`Return ${Return_Id || ''} created`);

    try {
      await downloadReturnPdf({
        Return_Id,
        date: createdItems?.[0]?.date || new Date(),
        items: createdItems || [],
      });
    } catch (pdfError) {
      console.log('Return PDF failed', pdfError);
      toast.error('Return saved, but the PDF could not be generated. Download it from the Returns list.');
    }

    setIsPorcessing(false);
    setTimeout(() => {
      navigate(`/main/inventory/Returns`);
    }, 1200);
  } catch (error) {
    toast.error(error?.response?.data?.error || error?.response?.data || 'Something went wrong');
    setIsPorcessing(false);
  }
};

const updateItem = async (data, setIsPorcessing) => {
  try {
    const result = await axios.put(`${END_POINT}/inventory/orders/${data._id}`, data, {
      headers: {
        Authorization: getJwtToken(),
      },
    });
    toast.success('Details Updated');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    toast.error(error?.response?.data || 'Something went wrong');
    setIsPorcessing(false);
    toast.error('Failed To Updated');
  }
};

const initialFormData = {
  order: '',
  nameOfMedicine: '',
  company: '',
  potencyOrPower: '',
  quantity: '',
  rate: '',
  typeOfMedicine: '',
  damage: '',
  reason: '',
  date: '',
  distributor: '',
};

const initialFormError = initialFormData;

export default function ReturnForm() {
  // define form state
  const [formData, setFormData] = useState([initialFormData]);
  const [formError, setFormError] = useState(initialFormError);
  const navigate = useNavigate();
  const { inventory_item_id, order_id } = useParams();
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isProcessing, setIsPorcessing] = useState(false);
  let [distributorsOptions, setDistributorsOptions] = useState([]);

  useEffect(() => {
    if (inventory_item_id === 'addNew') {
      if (order_id) {
        fetchOrders(order_id).then((e) => {
          setSelectedMedicine(e?.data);

          const mappedFormData = e?.data?.map((item) => {
            const { company, nameOfMedicine, potencyOrPower, typeOfMedicine, distributor, quantity, rate, Order_Id: order } = item;
            let damage = 0;
            return { company, nameOfMedicine, potencyOrPower, quantity, rate: rate ?? '', damage, distributor, damage, order, reason: '' };
          });
          if (mappedFormData && mappedFormData.length > 0) {
            setFormData(mappedFormData);
          } else {
            setFormData([initialFormData]);
          }
        });
      } else {
        setFormData([initialFormData]);
      }
    } else if (inventory_item_id) {
      fetchSingleItem(inventory_item_id).then((e) => {
        if (e?.data) setFormData(Array.isArray(e.data) ? e.data : [e.data]);
      });
    }
  }, [inventory_item_id, order_id]);

  useEffect(() => {
    if (selectedMedicine && selectedMedicine.length > 0) {
      fetchDistributor(selectedMedicine?.map((e) => e?.company)).then((e) => {
        setDistributorsOptions(e?.data || []);
      });
    } else {
      fetchAllDistributors().then((e) => {
        setDistributorsOptions(e?.data || []);
      });
    }
  }, [selectedMedicine]);

  // handel input change
  // Nothing is emailed any more, so the distributor is just a name on the order;
  // the server resolves it to a distributor record when it saves.
  const handleInputChange = (event, rowIndex) => {
    const { name, value } = event.target;
    const updatedFormData = [...formData];

    if (!updatedFormData[rowIndex]) {
      updatedFormData[rowIndex] = { ...initialFormData };
    }

    updatedFormData[rowIndex] = { ...updatedFormData[rowIndex], [name]: value };

    setFormData(updatedFormData);
  };

  // A medicine picked from the inventory fills in what it knows about itself;
  // the order, quantities, rate and damage stay with the user.
  const handleMedicinePick = (item, rowIndex) => {
    setFormData((prev) =>
      prev.map((row, index) =>
        index === rowIndex
          ? {
              ...row,
              nameOfMedicine: item?.nameOfMedicine || '',
              company: item?.company || '',
              potencyOrPower: item?.potencyOrPower || '',
              typeOfMedicine: item?.typeOfMedicine || '',
              distributor: item?.distributorName || row.distributor || '',
            }
          : row
      )
    );
  };

  const handleForm = () => {
    // The whole array goes up as one request so the server stamps a single
    // Return_Id across every line.
    const trimmedFormData = formData.map((row) => {
      const trimmedRow = { ...row };
      for (const key in trimmedRow) {
        if (typeof trimmedRow[key] === 'string') {
          trimmedRow[key] = trimmedRow[key].trim();
        }
      }
      return trimmedRow;
    });

    if (trimmedFormData.length === 0) {
      toast.error('Add at least one medicine');
      return false;
    }

    if (trimmedFormData.some((row) => !row.nameOfMedicine)) {
      toast.error('Every row needs a medicine name');
      return false;
    }

    if (trimmedFormData.some((row) => !(Number(row.damage) > 0))) {
      toast.error('Damage quantity must be greater than 0');
      return false;
    }

    setFormError(initialFormError);
    setIsPorcessing(true);

    if (inventory_item_id === 'addNew') {
      createItem(trimmedFormData, navigate, setIsPorcessing);
    } else {
      updateItem(trimmedFormData[0], setIsPorcessing);
    }
    return true;
  };

  const isNew = inventory_item_id === 'addNew';
  // Rows that came from an existing order keep their details locked; rows added
  // by hand below them stay fully editable.
  const lockedRows = selectedMedicine?.length || 0;
  const rows = formData;

  const removeRow = (index) => setFormData((prev) => prev.filter((_, i) => i !== index));

  return (
    <div className="min-h-screen w-full bg-slate-50 px-4 py-6 md:px-8">
      <div className="w-full">
        {/* Page header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
            >
              <FiArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">{isNew ? 'New Return' : 'Return Order'}</h1>
              <p className="text-xs text-slate-500">
                Enter the damaged quantity for each medicine. All rows are saved under a single return id, and the return PDF downloads automatically once you submit.
              </p>
            </div>
          </div>
          {rows?.length > 0 && (
            <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
              {rows.length} item{rows.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {formData ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleForm();
            }}
            className="space-y-6"
          >
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-4">
                <h2 className="text-sm font-semibold tracking-tight text-slate-900">Return Order Information</h2>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1400px] text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Order Id</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Name of Medicine</th>
                      <th className="w-40 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Order Date</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Potency / Power</th>
                      <th className="w-28 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Quantity</th>
                      <th className="w-32 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Rate / Unit</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Distributor</th>
                      <th className="w-28 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Damage</th>
                      <th className="w-56 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Reason</th>
                      <th className="w-14 px-3 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows?.map((item, index) => {
                      const isLocked = index < lockedRows;
                      return (
                        <tr key={index} className="align-top transition hover:bg-slate-50/70">
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="order" placeholder="Order id" value={item?.order || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <MedicineNameField
                              disabled={isLocked}
                              value={item?.nameOfMedicine}
                              onPick={(medicine) => handleMedicinePick(medicine, index)}
                              onType={(text) => handleInputChange({ target: { name: 'nameOfMedicine', value: text } }, index)}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} type="date" name="date" value={item?.date?.slice?.(0, 10) || item?.date || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="potencyOrPower" type="text" placeholder="Potency" value={item?.potencyOrPower || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput disabled={isLocked} name="quantity" type="number" placeholder="0" value={item?.quantity ?? ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            {/* Plain input: CustomInput has no step, and the default step of 1 would reject paise. */}
                            <div className="mt-1 w-full">
                              <input
                                name="rate"
                                type="number"
                                step="any"
                                min="0"
                                placeholder="From order"
                                value={item?.rate ?? ''}
                                onChange={(e) => handleInputChange(e, index)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-xs md:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <CustomSelect disabled={isLocked} onChange={(e) => handleInputChange(e, index)} options={distributorsOptions?.map((e) => e?.companyName)} value={item?.distributor || ''} name="distributor" placeholder="-- Select Distributor --" />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput name="damage" type="number" placeholder="0" value={item?.damage ?? ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <CustomInput name="reason" type="text" placeholder="Reason for return" value={item?.reason || ''} onChange={(e) => handleInputChange(e, index)} />
                          </td>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              aria-label="Remove item"
                              disabled={formData.length === 1}
                              onClick={() => removeRow(index)}
                              className="mt-1 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="border-t border-slate-200 px-6 py-4">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => [...prev, { ...initialFormData }])}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-sky-400 hover:bg-sky-50 hover:text-sky-700"
                >
                  <FiPlus className="h-4 w-4" />
                  Add Another Item
                </button>
              </div>
            </section>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pb-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="rounded-xl bg-sky-600 px-6 py-2 text-sm font-semibold text-white shadow-sm shadow-sky-600/30 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isProcessing ? 'Please wait...' : isNew ? 'Submit Return & Download PDF' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <CustomSpinner />
        )}
      </div>

      <ToastContainer />
    </div>
  );
}
